// meetingRooms/hooks/useServices.js
import { useState, useRef } from 'react';
import { updateResourceManagement } from 'api';

export const useServices = (allMeetingRooms, showSnackbar) => {
  const [servicesFormData, setServicesFormData] = useState({});
  const [originalServicesData, setOriginalServicesData] = useState({});
  const [servicesHasChanges, setServicesHasChanges] = useState({});
  const [isSavingServices, setIsSavingServices] = useState(false);
  const isSavingRef = useRef(false);

  const handleServicesFormChange = (meetingRoomSlug, newServicesData) => {
    setServicesFormData(prev => ({
      ...prev,
      [meetingRoomSlug]: newServicesData
    }));

    // Check if there are changes
    const originalData = originalServicesData[meetingRoomSlug];
    const hasChanges = originalData ? JSON.stringify(newServicesData) !== JSON.stringify(originalData) : false;

    setServicesHasChanges(prev => ({
      ...prev,
      [meetingRoomSlug]: hasChanges
    }));
  };

  // Separate save function for immediate saves (used by priority changes)
  const saveServices = async (meetingRoomSlug, servicesData) => {
    const meetingRoom = allMeetingRooms.find(mr => mr.slug === meetingRoomSlug);
    if (!meetingRoom) {
      throw new Error("Meeting room not found");
    }

    // Format services for API
    const formattedServices = servicesData.map((service, index) => ({
      slug: service.slug,
      priority: index,
      description: service.description || '',
      is_featured: service.is_featured || false
    }));

    const requestData = {
      resource: {
        id: meetingRoom.id, // Still need ID for the API
        services: formattedServices
      }
    };

    // Pass the slug as the second parameter
    const result = await updateResourceManagement(requestData, meetingRoomSlug);
    return result;
  };

  const handleSaveServices = async (meetingRoomSlug) => {
    const servicesData = servicesFormData[meetingRoomSlug];
    if (!servicesData) return;

    try {
      setIsSavingServices(true);
      isSavingRef.current = true;

      const meetingRoom = allMeetingRooms.find(mr => mr.slug === meetingRoomSlug);
      if (!meetingRoom) {
        throw new Error("Meeting room not found");
      }

      // Format services for API
      const formattedServices = servicesData.map((service, index) => ({
        slug: service.slug,
        priority: index,
        description: service.description || '',
        is_featured: service.is_featured || false
      }));

      const requestData = {
        resource: {
          id: meetingRoom.id, // Still need ID for the API
          services: formattedServices
        }
      };

      // Pass the slug as the second parameter
      const result = await updateResourceManagement(requestData, meetingRoomSlug);

      if (result && result.success) {
        showSnackbar("Services saved successfully", "success");

        // Update original data to match current form data
        setOriginalServicesData(prev => ({
          ...prev,
          [meetingRoomSlug]: [...servicesData]
        }));

        // Clear changes state
        setServicesHasChanges(prev => ({
          ...prev,
          [meetingRoomSlug]: false
        }));

      } else {
        showSnackbar(result?.error || "Failed to save services", "error");
      }
    } catch (error) {
      console.error("Error saving services:", error);
      showSnackbar("Failed to save services: " + error.message, "error");
    } finally {
      setIsSavingServices(false);
      isSavingRef.current = false;
    }
  };

  const handleCancelServices = (meetingRoomSlug) => {
    const originalData = originalServicesData[meetingRoomSlug];
    if (originalData) {
      setServicesFormData(prev => ({
        ...prev,
        [meetingRoomSlug]: [...originalData]
      }));

      setServicesHasChanges(prev => ({
        ...prev,
        [meetingRoomSlug]: false
      }));
    }
  };

  // Services reorder handlers - Save immediately and show toast
  const handleServiceMoveUp = async (meetingRoomSlug, serviceSlug) => {
    const services = servicesFormData[meetingRoomSlug] || [];
    const currentIndex = services.findIndex(service => service.slug === serviceSlug);
    if (currentIndex <= 0) return;

    const updatedServices = [...services];
    const temp = updatedServices[currentIndex];
    updatedServices[currentIndex] = updatedServices[currentIndex - 1];
    updatedServices[currentIndex - 1] = temp;

    // Update priorities
    updatedServices.forEach((service, idx) => {
      service.priority = idx;
    });

    // Update state immediately
    setServicesFormData(prev => ({
      ...prev,
      [meetingRoomSlug]: updatedServices
    }));

    // Save immediately and show toast
    try {
      setIsSavingServices(true);
      const result = await saveServices(meetingRoomSlug, updatedServices);
      if (result.success) {
        showSnackbar("Service priority updated successfully", "success");
        // Update original data to reflect saved state
        setOriginalServicesData(prev => ({
          ...prev,
          [meetingRoomSlug]: [...updatedServices]
        }));
        setServicesHasChanges(prev => ({
          ...prev,
          [meetingRoomSlug]: false
        }));
      } else {
        // Revert on failure
        setServicesFormData(prev => ({
          ...prev,
          [meetingRoomSlug]: services
        }));
        showSnackbar(result?.error || "Failed to update service priority", "error");
      }
    } catch (error) {
      // Revert on failure
      setServicesFormData(prev => ({
        ...prev,
        [meetingRoomSlug]: services
      }));
      showSnackbar("Failed to update service priority: " + error.message, "error");
    } finally {
      setIsSavingServices(false);
    }
  };

  const handleServiceMoveDown = async (meetingRoomSlug, serviceSlug) => {
    const services = servicesFormData[meetingRoomSlug] || [];
    const currentIndex = services.findIndex(service => service.slug === serviceSlug);
    if (currentIndex >= services.length - 1) return;

    const updatedServices = [...services];
    const temp = updatedServices[currentIndex];
    updatedServices[currentIndex] = updatedServices[currentIndex + 1];
    updatedServices[currentIndex + 1] = temp;

    // Update priorities
    updatedServices.forEach((service, idx) => {
      service.priority = idx;
    });

    // Update state immediately
    setServicesFormData(prev => ({
      ...prev,
      [meetingRoomSlug]: updatedServices
    }));

    // Save immediately and show toast
    try {
      setIsSavingServices(true);
      const result = await saveServices(meetingRoomSlug, updatedServices);
      if (result.success) {
        showSnackbar("Service priority updated successfully", "success");
        // Update original data to reflect saved state
        setOriginalServicesData(prev => ({
          ...prev,
          [meetingRoomSlug]: [...updatedServices]
        }));
        setServicesHasChanges(prev => ({
          ...prev,
          [meetingRoomSlug]: false
        }));
      } else {
        // Revert on failure
        setServicesFormData(prev => ({
          ...prev,
          [meetingRoomSlug]: services
        }));
        showSnackbar(result?.error || "Failed to update service priority", "error");
      }
    } catch (error) {
      // Revert on failure
      setServicesFormData(prev => ({
        ...prev,
        [meetingRoomSlug]: services
      }));
      showSnackbar("Failed to update service priority: " + error.message, "error");
    } finally {
      setIsSavingServices(false);
    }
  };

  const handleAddService = (meetingRoomSlug, service) => {
    const currentServices = servicesFormData[meetingRoomSlug] || [];
    if (currentServices.some(s => s.slug === service.slug)) return; // Prevent duplicates

    const newService = {
      slug: service.slug,
      name: service.name,
      priority: currentServices.length,
      is_featured: false,
      description: service.description || ''
    };

    const updatedServices = [...currentServices, newService];
    handleServicesFormChange(meetingRoomSlug, updatedServices);
  };

  const handleRemoveService = (meetingRoomSlug, serviceSlug) => {
    const services = servicesFormData[meetingRoomSlug] || [];
    const updatedServices = services.filter(s => s.slug !== serviceSlug);
    handleServicesFormChange(meetingRoomSlug, updatedServices);
  };

  const initializeServices = (meetingRooms) => {
    const newServicesFormData = {};
    const newOriginalServicesData = {};

    meetingRooms.forEach(meetingRoom => {
      const servicesData = meetingRoom.services ? meetingRoom.services.map(service => ({
        ...service,
        priority: service.priority || 0,
        is_featured: service.is_featured || false
      })).sort((a, b) => a.priority - b.priority) : [];

      if (!servicesFormData[meetingRoom.slug]) {
        newServicesFormData[meetingRoom.slug] = servicesData;
        newOriginalServicesData[meetingRoom.slug] = [...servicesData];
      } else {
        newServicesFormData[meetingRoom.slug] = servicesFormData[meetingRoom.slug];
        newOriginalServicesData[meetingRoom.slug] = originalServicesData[meetingRoom.slug];
      }
    });

    setServicesFormData(newServicesFormData);
    setOriginalServicesData(newOriginalServicesData);
  };

  // Service description save handler - Save immediately and show toast
  const handleServiceDescriptionSave = async (meetingRoomSlug, serviceSlug, newDescription) => {
    const services = servicesFormData[meetingRoomSlug] || [];
    const serviceIndex = services.findIndex(service => service.slug === serviceSlug);
    if (serviceIndex === -1) return;

    const updatedServices = [...services];
    updatedServices[serviceIndex] = {
      ...updatedServices[serviceIndex],
      description: newDescription
    };

    // Update state immediately
    setServicesFormData(prev => ({
      ...prev,
      [meetingRoomSlug]: updatedServices
    }));

    // Save immediately and show toast
    try {
      setIsSavingServices(true);
      const result = await saveServices(meetingRoomSlug, updatedServices);
      if (result.success) {
        showSnackbar("Service description updated successfully", "success");
        // Update original data to reflect saved state
        setOriginalServicesData(prev => ({
          ...prev,
          [meetingRoomSlug]: [...updatedServices]
        }));
        setServicesHasChanges(prev => ({
          ...prev,
          [meetingRoomSlug]: false
        }));
      } else {
        // Revert on failure
        setServicesFormData(prev => ({
          ...prev,
          [meetingRoomSlug]: services
        }));
        showSnackbar(result?.error || "Failed to update service description", "error");
      }
    } catch (error) {
      // Revert on failure
      setServicesFormData(prev => ({
        ...prev,
        [meetingRoomSlug]: services
      }));
      showSnackbar("Failed to update service description: " + error.message, "error");
    } finally {
      setIsSavingServices(false);
    }
  };

  return {
    servicesFormData,
    servicesHasChanges,
    isSavingServices,
    isSavingRef,
    handleServicesFormChange,
    handleSaveServices,
    handleCancelServices,
    handleServiceMoveUp,
    handleServiceMoveDown,
    handleAddService,
    handleRemoveService,
    initializeServices,
    handleServiceDescriptionSave
  };
};